import React, {
  useState,
  useContext,
  useRef,
  useEffect,
  useMemo,
  useCallback,
} from "react";
import {
  Link,
  useParams,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import { ShopContext } from "../context/ShopContext";
import DropdownMenu from "./DropdownMenu";
import textures from "../data/textures";
import SEO from "../context/SEO";
import "./ProductView.css";
import { trackAddToCart } from "../helpers/analytics";
import styles from "./ProductViewGrid.module.scss";
import ProductLinks from "./ProductLinks";
import useVariantStock from "../Utilities/useVariantStock";

// ==================== HELPER ====================
// Uses the exact same alias logic as your swatches in textures.js
const getTextureKey = (colour) => {
  if (!colour) return "";
  const normalized = colour.replace(/\s+/g, "").toLowerCase();
  return textures[normalized] !== undefined ? textures[normalized] : normalized;
};

// Gallery photos sit on the grey shimmer until they've downloaded, then fade
// in. Done with classes (no state) so loading photos never re-render the page;
// the ref covers photos already in the browser cache, which can finish loading
// before React attaches onLoad.
const markImageLoaded = (img) => {
  img.classList.add(styles.imageLoaded);
  img.parentElement?.classList.add(styles.imageLoaded);
};
const handleImageLoad = (e) => markImageLoaded(e.currentTarget);
const checkCachedImage = (img) => {
  if (img && img.complete && img.naturalWidth > 0) markImageLoaded(img);
};

// --- SUB-COMPONENT: Defined OUTSIDE to prevent re-render bugs ---
function CrossSellItem({
  cs,
  index,
  addCrossSells,
  toggleAddCrossSell,
  variationIndex,
  onDataUpdate,
}) {
  const { stock, price, loading, error } = useVariantStock(
    cs.product,
    variationIndex,
  );

  useEffect(() => {
    onDataUpdate(index, { stock, price, loading, error });
  }, [stock, price, loading, error, index, onDataUpdate]);

  const localStock =
    cs.product?.variations?.[variationIndex]?.quantity?.inStock ?? 0;
  const isLive = typeof stock === "number";
  const isInStock = isLive ? stock > 0 : localStock > 0;
  const isDisabled = !isInStock || loading || error || price === null;
  const isAdded = addCrossSells[index] || false;

  const variant = cs.product.variations[variationIndex];
  const imageSrc =
    variant?.coverPhoto ||
    variant?.imageSrc?.[0] ||
    cs.product.variations[0]?.imageSrc?.[0];

  return (
    <div className={styles.crossSellRow}>
      {/* Left Side: Image and Name */}
      <div className={styles.crossSellInfo}>
        <div className={styles.crossSellImage}>
          {imageSrc && <img src={imageSrc} alt={cs.product.name} />}
        </div>
        <div className={styles.crossSellName}>
          {cs.product.name}
          {!isInStock && !loading && (
            <span className={styles.outOfStockLabel}> (Out of Stock)</span>
          )}
        </div>
      </div>
      {/* Right Side: Price and Button */}
      <div className={styles.crossSellAction}>
        <div className={styles.crossSellPrice}>
          {loading
            ? "..."
            : price !== null
              ? `${Number(price).toLocaleString("de-DE", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })} €`
              : "--,--"}
        </div>
        <button
          className={`${styles.crossSellButton} ${isAdded ? styles.added : ""}`}
          onClick={() => toggleAddCrossSell(index, !isAdded)}
          disabled={isDisabled}
        >
          {isAdded ? "Remove" : "+ Add"}
        </button>
      </div>
    </div>
  );
}

// --- MAIN COMPONENT ---
function ProductViewGrid() {
  let navigate = useNavigate();
  const { products, addToCart, toggleCartOverlay } = useContext(ShopContext);
  const [selectedVariation, setSelectedVariation] = useState(0);
  const { productId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const product = useMemo(
    () => products.find((p) => p.id === productId),
    [products, productId],
  );

  // Open on a specific variation when linked with ?variation=<index>
  // (e.g. from the homepage image links), clamped to a valid index.
  useEffect(() => {
    if (!product) return;
    const v = parseInt(searchParams.get("variation") ?? "", 10);
    if (!Number.isNaN(v) && v >= 0 && v < product.variations.length) {
      setSelectedVariation(v);
    }
  }, [product, searchParams]);

  // Keep the URL in sync with the chosen colour/size so the address bar (or the
  // phone's share sheet) always links to that exact variation. `replace` so
  // clicking through colours doesn't fill up the back-button history.
  const selectVariation = (index) => {
    setSelectedVariation(index);
    const params = new URLSearchParams(searchParams);
    if (index === 0) params.delete("variation");
    else params.set("variation", String(index));
    setSearchParams(params, { replace: true });
  };

  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [detailsVisible, setDetailsVisible] = useState(false);
  const [quantity, setQuantity] = useState(0);
  const [collectionProducts, setCollectionProducts] = useState([]);
  const [familyProducts, setFamilyProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isNotifyModalVisible, setIsNotifyModalVisible] = useState(false);
  const [addCrossSells, setAddCrossSells] = useState({});
  const [crossSellVariationIndices, setCrossSellVariationIndices] = useState(
    {},
  );
  const [crossSellLiveStates, setCrossSellLiveStates] = useState({});

  const carouselRef = useRef(null);
  const preferencesRef = useRef(null);
  const verticalCarouselRef = useRef(null);
  const imageScrollRef = useRef(null);

  const crossSellProducts = useMemo(() => {
    return (product?.crossSells || [])
      .map((cs) => {
        const csProduct = products.find((p) => p.id === cs.productId);
        return { ...cs, product: csProduct };
      })
      .filter((cs) => cs.product);
  }, [product, products]);

  // --- Main Product Hook ---
  const {
    stock: mainLiveStock,
    price: mainLivePrice,
    loading: mainStockLoading,
    error: mainStockError,
  } = useVariantStock(product, selectedVariation);

  // --- NEW: Callback to receive stock/price from CrossSellItems ---
  const handleCrossSellDataUpdate = useCallback((index, data) => {
    setCrossSellLiveStates((prev) => {
      if (JSON.stringify(prev[index]) === JSON.stringify(data)) return prev;
      return { ...prev, [index]: data };
    });
  }, []);

  // --- Logic Calculations ---
  const mainInStock =
    typeof mainLiveStock === "number"
      ? mainLiveStock > 0
      : (product?.variations?.[selectedVariation]?.quantity?.inStock ?? 1) > 0;

  const mainMaxQuantity = mainStockLoading
    ? (product?.variations?.[selectedVariation]?.quantity?.inStock ?? 1)
    : Math.max(1, mainLiveStock ?? 1);

  let overallMaxQuantity = mainMaxQuantity;
  let overallInStock = mainInStock;
  let totalPrice = (mainLivePrice ?? 0) * quantity;

  Object.keys(addCrossSells).forEach((index) => {
    if (addCrossSells[index]) {
      const csData = crossSellLiveStates[index] || {};
      const variationIndex = crossSellVariationIndices[index] ?? 0;
      const localStock =
        crossSellProducts[index]?.product?.variations?.[variationIndex]
          ?.quantity?.inStock ?? 1;

      const itemStock =
        typeof csData.stock === "number" ? csData.stock : localStock;
      const itemInStock = itemStock > 0;

      overallMaxQuantity = Math.min(overallMaxQuantity, itemStock);
      overallInStock = overallInStock && itemInStock;
      totalPrice += (csData.price ?? 0) * quantity;
    }
  });

  const hasValidPrices =
    mainLivePrice !== null &&
    Object.keys(addCrossSells).every((index) =>
      addCrossSells[index] ? crossSellLiveStates[index]?.price !== null : true,
    );

  useEffect(() => {
    if (product) {
      setIsLoading(false);
      const familyProds = products.filter(
        (familyProduct) => familyProduct.family === product.family,
      );
      const collectionProds = products.filter((collectionProduct) =>
        collectionProduct.collection.some((collection) =>
          product.collection.includes(collection),
        ),
      );
      setCollectionProducts(collectionProds);
      setFamilyProducts(familyProds);
    } else {
      setIsLoading(true);
    }
  }, [product, products]);

  const isAnyCrossSellLoading = Object.values(crossSellLiveStates).some(
    (state) => state.loading,
  );

  useEffect(() => {
    if (mainStockLoading || isAnyCrossSellLoading) return;
    if (overallInStock) {
      setQuantity((prev) => (prev === 0 ? 1 : prev));
    } else {
      setQuantity(0);
    }
  }, [overallInStock, mainStockLoading, isAnyCrossSellLoading]);

  useEffect(() => {
    if (quantity > overallMaxQuantity) {
      setQuantity(overallMaxQuantity);
    }
  }, [overallMaxQuantity, quantity]);

  // Reset the image gallery to the first photo whenever the variation changes.
  useEffect(() => {
    setActiveImageIndex(0);
    if (imageScrollRef.current) {
      imageScrollRef.current.scrollTo({ top: 0 });
    }
  }, [selectedVariation]);

  // ==================== MATCH VARIANTS FOR CROSS-SELLS ====================
  // Uses your textures.js alias system so "Blue Moon" matches "blue", "Smoke Grey" matches "grey", etc.
  useEffect(() => {
    if (!product) return;

    const newVariationIndices = {};
    const newAddCrossSells = { ...addCrossSells };

    crossSellProducts.forEach((cs, index) => {
      if (cs.matchVariant) {
        const mainColour =
          product.variations[selectedVariation]?.colour ||
          product.variations[selectedVariation]?.size ||
          "";

        const mainKey = getTextureKey(mainColour);

        const varIndex = cs.product.variations.findIndex((v) => {
          const vColour = v.colour || v.size || "";
          return getTextureKey(vColour) === mainKey;
        });

        newVariationIndices[index] = varIndex >= 0 ? varIndex : 0;

        if (varIndex === -1) {
          newAddCrossSells[index] = false;
        }
      } else {
        newVariationIndices[index] = 0;
      }
    });

    setCrossSellVariationIndices(newVariationIndices);
    setAddCrossSells(newAddCrossSells);
  }, [selectedVariation, crossSellProducts, product]);

  if (!product && !isLoading) {
    return <div>Product not found</div>;
  }
  if (isLoading) {
    // Placeholder in the page's own layout (photo panel + shimmering bars)
    // so the product fades in where the user is already looking.
    return (
      <div>
        <DropdownMenu showElements={true} backVisible={true} />
        <div
          className={styles.grid}
          aria-busy="true"
          aria-label="Loading product"
        >
          <div
            className={`${styles.productImageContainer} ${styles.skeletonImage}`}
          />
          <div className={styles.productDetailsContainer}>
            <div className={`${styles.skeleton} ${styles.skeletonTitle}`} />
            <div className={`${styles.skeleton} ${styles.skeletonPrice}`} />
            <div className={styles.skeletonSwatches}>
              {[0, 1, 2, 3].map((i) => (
                <span key={i} className={styles.skeleton} />
              ))}
            </div>
            <div className={`${styles.skeleton} ${styles.skeletonText}`} />
            <div className={`${styles.skeleton} ${styles.skeletonText}`} />
            <div className={`${styles.skeleton} ${styles.skeletonButton}`} />
          </div>
        </div>
      </div>
    );
  }

  const handleNotifyMe = () => {
    setIsNotifyModalVisible(true);
  };

  const closeNotifyModal = () => {
    setIsNotifyModalVisible(false);
  };

  const handleSubmitNotification = (event) => {
    event.preventDefault();
    closeNotifyModal();
  };

  const handleAddToCart = () => {
    const clampedQuantity = Math.min(quantity, overallMaxQuantity);
    addToCart(product, selectedVariation, clampedQuantity);
    crossSellProducts.forEach((cs, index) => {
      if (addCrossSells[index]) {
        const varIndex = crossSellVariationIndices[index] ?? 0;
        addToCart(cs.product, varIndex, clampedQuantity);
      }
    });
    toggleCartOverlay();
    trackAddToCart(product, selectedVariation, clampedQuantity);
  };

  let buttonClass = styles.basicButton;
  let buttonOnClick = handleAddToCart;
  let buttonText =
    mainStockLoading || isAnyCrossSellLoading
      ? "Loading price..."
      : `Add to basket (${totalPrice.toFixed(2).replace(".", ",")} €)`;

  if (mainStockLoading || isAnyCrossSellLoading) {
    buttonClass = `${styles.basicButton} ${styles.loading}`;
  } else if (
    mainStockError ||
    Object.values(crossSellLiveStates).some((s) => s.error) ||
    !hasValidPrices
  ) {
    buttonText = "Unavailable";
    buttonClass = `${styles.basicButton} ${styles.disabled}`;
    buttonOnClick = handleNotifyMe;
  } else if (!overallInStock) {
    buttonText = "Notify me";
    buttonClass = `${styles.basicButton} ${styles.disabled}`;
    buttonOnClick = handleNotifyMe;
  }

  const decrementQuantity = () => {
    if (overallInStock) {
      setQuantity((prev) => Math.max(1, prev - 1));
    }
  };

  const incrementQuantity = () => {
    if (overallInStock) {
      setQuantity((prev) => Math.min(overallMaxQuantity, prev + 1));
    }
  };

  const variationLabel =
    product.variationType === "Colour"
      ? product.variations[selectedVariation].colour
      : product.variations[selectedVariation].size;

  const toggleAddCrossSell = (index, checked) => {
    setAddCrossSells((prev) => ({ ...prev, [index]: checked }));
  };

  // All photos shown in the vertical image scroller (variation + lifestyle),
  // used both to render and to drive the progress dots.
  const galleryImages = [
    ...(product.variations[selectedVariation].imageSrc || []),
    ...(product.lifestyleImages || []),
  ];

  // Track which photo is in view (each image is one scroll "page").
  const handleImageScroll = () => {
    const el = imageScrollRef.current;
    if (!el || el.clientHeight === 0) return;
    const idx = Math.round(el.scrollTop / el.clientHeight);
    const clamped = Math.max(0, Math.min(idx, galleryImages.length - 1));
    setActiveImageIndex((prev) => (prev === clamped ? prev : clamped));
  };

  const scrollToImage = (index) => {
    const el = imageScrollRef.current;
    if (!el) return;
    el.scrollTo({ top: index * el.clientHeight, behavior: "smooth" });
  };

  return (
    <div>
      <DropdownMenu showElements={true} backVisible={true} />
      {/* Product + variation first, brand last: Safari's shortened tabs and
          Google's truncated results show the part that actually differs. */}
      <SEO
        title={`${[product.name, variationLabel].filter(Boolean).join(" ")} – Cork Matters`}
      />
      <div className={`${styles.grid} ${styles.fadeInContent}`}>
        <div
          className={styles.productImageContainer}
          ref={imageScrollRef}
          onScroll={handleImageScroll}
          data-lenis-prevent
        >
          {/* Keyed by src so switching colour mounts fresh photos that fade in
              again, rather than reusing an <img> already marked as loaded. */}
          {product.variations[selectedVariation].imageSrc.map((src, index) => (
            <div key={`${index}-${src}`} className={styles.imageSlot}>
              <img
                src={src}
                alt={product.name}
                onLoad={handleImageLoad}
                ref={checkCachedImage}
              />
            </div>
          ))}
          {product.lifestyleImages &&
            product.lifestyleImages.length > 0 &&
            product.lifestyleImages.map((src, index) => (
              <div
                key={`lifestyle-${index}-${src}`}
                className={styles.imageSlot}
              >
                <img
                  src={src}
                  alt={`${product.name} lifestyle`}
                  onLoad={handleImageLoad}
                  ref={checkCachedImage}
                />
              </div>
            ))}
        </div>

        {galleryImages.length > 1 && (
          <div className={styles.imageDots}>
            {galleryImages.map((_, index) => (
              <button
                key={index}
                type="button"
                aria-label={`View image ${index + 1} of ${galleryImages.length}`}
                className={`${styles.imageDot} ${
                  index === activeImageIndex ? styles.imageDotActive : ""
                }`}
                onClick={() => scrollToImage(index)}
              />
            ))}
          </div>
        )}

        <div className={styles.productDetailsContainer} data-lenis-prevent>
          <div className={styles.title}>
            {product.name} {product.size ? `(${product.size})` : ""}
            <span className={styles.colourSpan}> {variationLabel} </span>
          </div>

          <div className={styles.price}>
            {mainStockLoading ? (
              "Loading price..."
            ) : mainLivePrice !== null ? (
              <>
                {Number(mainLivePrice).toLocaleString("de-DE", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}{" "}
                €
              </>
            ) : (
              "€--,--"
            )}
          </div>

          <div className={styles.variationsTitle}>Variations:</div>
          <div className={styles.variations}>
            {product.variations.map((variation, index) => (
              <div
                key={index}
                onClick={() => selectVariation(index)}
                className={
                  index === selectedVariation ? styles.selectedVariation : ""
                }
              >
                {product.variationType === "Colour" ? (
                  <img
                    src={
                      variation.colour
                        ? textures[
                            variation.colour.replace(/\s+/g, "").toLowerCase()
                          ]
                        : ""
                    }
                    className={styles.texture}
                    alt={variation.colour}
                  />
                ) : (
                  <span className={styles.sizeLabel}>{variation.size}</span>
                )}
              </div>
            ))}
          </div>

          <div className={styles.shortDescription}>
            {product.shortDescription}
          </div>

          <table className={styles.mainProductTable}>
            <tbody>
              <tr>
                <th>Height</th>
                <th>Width</th>
                <th>Length</th>
                <th>Weight</th>
                <th className={styles.special}></th>
              </tr>
              <tr>
                <td>
                  {product.variations[selectedVariation].dimensions.height} mm
                </td>
                <td>
                  {product.variations[selectedVariation].dimensions.width} mm
                </td>
                <td>
                  {product.variations[selectedVariation].dimensions.length} mm
                </td>
                <td colSpan="2">
                  {product.variations[selectedVariation].mass} kg
                </td>
              </tr>
            </tbody>
          </table>

          <div className={styles.quantityContainer}>
            <div className={styles.quantityButton} onClick={decrementQuantity}>
              -
            </div>
            <div className={styles.quantity}>{quantity}</div>
            <div
              className={`${styles.quantityButton} ${
                quantity >= overallMaxQuantity ? styles.disabled : ""
              }`}
              onClick={incrementQuantity}
              disabled={quantity >= overallMaxQuantity}
            >
              +
            </div>
          </div>

          {quantity >= overallMaxQuantity &&
            overallMaxQuantity > 0 &&
            overallInStock && (
              <div className={styles.stockMessage}>
                Max stock reached ({overallMaxQuantity} available)
              </div>
            )}

          {crossSellProducts.length > 0 && (
            <div className={styles.crossSellContainer}>
              <h4>Recommended Add-Ons</h4>
              {crossSellProducts.map((cs, index) => (
                <CrossSellItem
                  key={cs.product.id}
                  cs={cs}
                  index={index}
                  addCrossSells={addCrossSells}
                  toggleAddCrossSell={toggleAddCrossSell}
                  variationIndex={crossSellVariationIndices[index] || 0}
                  onDataUpdate={handleCrossSellDataUpdate}
                />
              ))}
            </div>
          )}

          <button
            className={buttonClass}
            onClick={buttonOnClick}
            disabled={
              mainStockLoading ||
              isAnyCrossSellLoading ||
              !overallInStock ||
              !hasValidPrices
            }
          >
            {buttonText}
          </button>

          <ProductLinks
            productDescription={product.description}
            technicalDetails={product.technicalDetails}
            additionalInformation={product.additionalInformation}
            stock={mainLiveStock}
            stockLoading={mainStockLoading}
            stockError={mainStockError}
          />

          <div className={styles.carouselTitle}>Finish the Family</div>
          <div className={styles.collectionContainer}>
            <div className={styles.productCarousel} ref={carouselRef}>
              {collectionProducts.map((otherProduct) => (
                <div key={otherProduct.id} className={styles.carouselItem}>
                  <Link to={`/product/${otherProduct.id}`}>
                    <img
                      src={otherProduct.variations[0].coverPhoto}
                      alt={otherProduct.name}
                    />
                  </Link>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {isNotifyModalVisible && (
        <div className={styles.notifyModal}>
          <div className={styles.notifyModalContent}>
            <h2>Notify Me When Available</h2>
            <form onSubmit={handleSubmitNotification}>
              <input type="email" placeholder="Enter your email" required />
              <button type="submit">Submit</button>
              <button type="button" onClick={closeNotifyModal}>
                Cancel
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default ProductViewGrid;
